import { translateImportJobError } from "helpers/importJobError";
import { t } from "i18next";
import { toast } from "react-toastify";
import type { Job } from "store/jobsStore";
import { fetchJob, ProcessingJobStatus } from "store/jobsStore";
import type { AppDispatch } from "store/store";

const POLL_INTERVAL_MS = 6000;

const activePolls = new Map<number, number>();

function progressLabel(progress: string | null | undefined) {
    const numeric = String(progress ?? "")
        .replace(/%/g, "")
        .trim();
    return t("ImportData.ImportInProgress", { progress: numeric === "" ? 0 : numeric });
}

/**
 * Loading toast that stays open and shows the job percentage.
 * Polling lives outside the toast content: react-toastify remounts the toast on
 * each update, which used to unmount the previous listener and close it.
 */
export function showImportProgressToast(jobId: number, dispatch: AppDispatch) {
    if (activePolls.has(jobId)) return;

    const toastId = `import-progress-${jobId}`;
    let lastLabel = progressLabel("0");

    toast.loading(lastLabel, {
        toastId,
        autoClose: false,
        closeOnClick: false,
        closeButton: true,
        hideProgressBar: true,
        // Keeps the progress bar from treating an undefined value as "finished" and closing the toast.
        progress: 0,
    });

    const stop = () => {
        const timer = activePolls.get(jobId);
        if (timer != null) window.clearInterval(timer);
        activePolls.delete(jobId);
    };

    const poll = () => {
        if (!toast.isActive(toastId)) {
            stop();
            return;
        }

        dispatch(fetchJob({ jobId })).then(({ payload, meta }) => {
            if (!toast.isActive(toastId)) {
                stop();
                return;
            }
            if (meta.requestStatus !== "fulfilled") return;

            const job = payload as Job;

            if (job.status === ProcessingJobStatus.DONE) {
                stop();
                toast.update(toastId, {
                    render: `${t("ImportData.ImportCompleted")}`,
                    type: "success",
                    isLoading: false,
                    autoClose: 15000,
                    closeOnClick: true,
                    closeButton: true,
                    hideProgressBar: false,
                });
                return;
            }

            if (job.status === ProcessingJobStatus.FAILED || job.status === ProcessingJobStatus.REVERTED) {
                stop();
                const failureDetail = translateImportJobError(t, {
                    errorCode: job.metadata?.errorCode,
                    errorMessage: job.metadata?.errorMessage,
                    errorParams: job.metadata?.errorParams,
                });
                toast.update(toastId, {
                    render: failureDetail
                        ? `${t("ImportData.ImportFailed")} — ${failureDetail}`
                        : `${t("ImportData.ImportFailed")}`,
                    type: "error",
                    isLoading: false,
                    autoClose: 15000,
                    closeOnClick: true,
                    closeButton: true,
                    hideProgressBar: false,
                });
                return;
            }

            const nextLabel = progressLabel(job.progress);
            if (nextLabel === lastLabel) return;
            lastLabel = nextLabel;

            toast.update(toastId, {
                render: nextLabel,
                isLoading: true,
                autoClose: false,
                closeOnClick: false,
                closeButton: true,
                hideProgressBar: true,
                progress: 0,
            });
        });
    };

    poll();
    activePolls.set(jobId, window.setInterval(poll, POLL_INTERVAL_MS));
}
